import { Injectable, OnDestroy } from '@angular/core';

import { IPointsHistoryItem, IPointsRedeemedResult } from 'libs/domain';
import { ReplaySubject } from 'rxjs';

import { RootService } from '@app/src/app/minimal/services/RootService';

import { SystemAlertModalService } from '@shared/components/system-alert-modal';
import { SystemAlertSnackBarService } from '@shared/components/system-alert-snackbar';
import { ReportActionService } from '@shared/services/reports';

import { PmReportsHistoryMessages } from '../components/pm-reports-history/pm-reports-history.constants';
import { PmReportsRedemptionHistoryMessages } from '../components/pm-reports-points-redeemed/pm-reports-redemption-history.constants';
import { PmReportsService } from './pm-reports.service';

@Injectable()
export class PmReportsHistoryActionService
  extends ReportActionService<IPointsHistoryItem | IPointsRedeemedResult>
  implements OnDestroy
{
  private _destroyed = new ReplaySubject<void>(1);

  constructor(
    public snackbar: SystemAlertSnackBarService,
    public alertModal: SystemAlertModalService,
    private _rootService: RootService,
    private _pmReportsService: PmReportsService,
  ) {
    super(snackbar, alertModal);
  }

  ngOnDestroy(): void {
    this._destroyed.next();
    this._destroyed.complete();
  }

  /** Abstract methods */
  public setTotalSelectable(total: number): void {
    this.totalSelectable = total;
  }

  /** Action methods */
  public async archivePoints(): Promise<boolean> {
    if (!this.isSelectionValid()) return;
    try {
      const confirmed = await this.openAlertModal(
        PmReportsHistoryMessages.ARCHIVE_HEADING,
        '',
        PmReportsHistoryMessages.ARCHIVE_LABEL,
      );

      if (confirmed) {
        const data = this.getSelection() as IPointsHistoryItem[];
        await this._pmReportsService.archivePointAction(data);
      }
      this._rootService.addLoadingPromise(this._clearSelectionAndRefetch());
      return true;
    } catch (e) {
      this.openSnackBar(`Unable to archive consequence(s): ${e}`, 'error');
      return false;
    }
  }

  public async archivePointRedemptions(): Promise<boolean> {
    if (!this.isSelectionValid()) return;
    try {
      const confirmed = await this.openAlertModal(
        PmReportsRedemptionHistoryMessages.ARCHIVE_HEADING,
        '',
        PmReportsRedemptionHistoryMessages.ARCHIVE_LABEL,
      );

      if (confirmed) {
        const data = this.getSelection() as IPointsRedeemedResult[];
        await this._pmReportsService.archivePointRedemption(data);
      }
      this._rootService.addLoadingPromise(this._clearSelectionAndRefetch());
      return true;
    } catch (e) {
      this.openSnackBar(`Unable to archive consequence(s): ${e}`, 'error');
      return false;
    }
  }

  public getSelectionMessage(): string {
    if (this.isEmpty) return '';

    const selection = this.getSelection();
    const message = `${selection.length} selected`;
    return message;
  }

  private async _clearSelectionAndRefetch() {
    this._pmReportsService.applyFilter();
    this.clearSelection();
  }
}
